<template>
    <NewQuote
        :show="show"
        :material-types="materialTypes"
        :material-size-types="materialSizeTypes"
        :receipt-logs="receiptLogs"
        :loading-receipt-log-results="loadingReceiptLogResults"
        :loading-customer-results="loadingCustomerResults"
        :allow-customer-select="allowCustomerSelect"
        :customers="customers"
        :customer="me.customer_name"
        :designer="me.designer_name"
        @update:show="$emit('update:show', $event)"
        @searchReceiptLogs="executeSearchReceiptLogs"
        @searchCustomers="executeSearchCustomers"
        @addQuote="addNewQuote"
    />
</template>
<script>
import SmartyStreetsSDK from "smartystreets-javascript-sdk";
import { mapActions } from "vuex";
import swal from "sweetalert2";
import NewQuote from "./_components/NewQuote.vue";
import { store, getters } from "src/global-store.js";

const SmartyStreetsCore = SmartyStreetsSDK.core;
const { Lookup } = SmartyStreetsSDK.usStreet;

const credentials = new SmartyStreetsCore.SharedCredentials(
    process.env.VUE_APP_SMARTY_WEBSITE_KEY,
);
const usStreetClientBuilder = new SmartyStreetsCore.ClientBuilder(credentials);
const streetClient = usStreetClientBuilder.buildUsStreetApiClient();

export default {
    name: "NewQuoteModule",
    components: { NewQuote },
    props: {
        show: {
            type: Boolean,
            required: true,
        },
    },
    data() {
        return {
            customers: [],
            materialTypes: [],
            materialSizeTypes: ["Boxes", "Cubes", "Each", "Pallets"],
            receiptLogs: [],
            loadingReceiptLogResults: false,
            loadingCustomerResults: false,
        };
    },
    computed: {
        me() {
            return store.me;
        },
        hasCompanyRole() {
            return getters.hasCompanyRole();
        },
        allowCustomerSelect() {
            return this.hasCompanyRole;
        },
    },
    async created() {
        this.materialTypes = await this.fetchMaterialTypes();
        if (this.allowCustomerSelect) {
            // only get customers if we can select them
            this.customers = await this.searchCustomers("");
        } else {
            // don't search receipt logs immediately if we have to select a customer
            // to reduce load time
            this.receiptLogs = await this.searchReceiptLogs("");
        }
    },
    methods: {
        ...mapActions("$_quote", [
            "fetchMaterialTypes",
            "searchReceiptLogs",
            "addQuote",
        ]),
        ...mapActions("customer", ["searchCustomers"]),
        async executeSearchReceiptLogs(search, customerID) {
            this.loadingReceiptLogResults = true;
            this.receiptLogs = await this.searchReceiptLogs({
                search,
                customerID,
            });
            this.loadingReceiptLogResults = false;
        },
        async executeSearchCustomers(search) {
            this.loadingCustomerResults = true;
            this.customers = await this.searchCustomers(search);
            this.loadingCustomerResults = false;
        },
        async addNewQuote(quote) {
            let sendRequest = false;
            if (process.env.NODE_ENV !== "production") {
                // bypass address verification
                sendRequest = true;
                quote.deliveryAddressVerified = false;
            } else {
                const lookup = new Lookup();
                let addressLookup;
                lookup.street = quote.deliveryAddress;
                lookup.city = quote.deliveryCity;
                lookup.zipCode = quote.deliveryZip;
                lookup.state = "CO";
                lookup.match = "strict";
                const response = await streetClient.send(lookup);
                addressLookup = response.lookups[0];
                const { result } = addressLookup;

                // there was a match
                if (result.length) {
                    const candidate = result[0];
                    if (
                        addressLookup.street !== candidate.deliveryLine1 ||
                        addressLookup.city !== candidate.components.cityName ||
                        addressLookup.zipCode !== candidate.components.zipCode
                    ) {
                        // entered address is not the same as the candidate
                        const { value } = await swal({
                            title: "Delivery Address Verification",
                            allowEscapeKey: false,
                            allowOutsideClick: false,
                            showConfirmButton: true,
                            confirmButtonText: "Use verified address",
                            confirmButtonColor: "#3085d6",
                            showCancelButton: true,
                            cancelButtonText: "Use my original address",
                            cancelButtonColor: "#ff8d72",
                            type: "warning",
                            html: `
              <div class="row mb-3">
                <div class="col-12"><h3 class="mb-0">Original Address:</h3></div>
                <div class="col-12">${addressLookup.street}</div>
                <div class="col-12">${addressLookup.city}, ${addressLookup.state} ${addressLookup.zipCode}</div>
              </div>
              <div class="row">
                <div class="col-12"><h3 class="mb-0">Verified Address:</h3></div>
                <div class="col-12">${candidate.deliveryLine1}</div>
                <div class="col-12">${candidate.lastLine}</div>
              </div>
              `,
                        });
                        if (value) {
                            quote.deliveryAddress = candidate.deliveryLine1;
                            quote.deliveryCity = candidate.components.cityName;
                            quote.deliveryZip = candidate.components.zipCode;
                            quote.deliveryAddressVerified = true;
                            sendRequest = true;
                        } else {
                            quote.deliveryAddressVerified = false;
                            sendRequest = true;
                        }
                    } else {
                        // perfect match
                        quote.deliveryAddressVerified = true;
                        sendRequest = true;
                    }
                } else {
                    const { value } = await swal({
                        title: "Delivery Address Not Verified",
                        allowEscapeKey: false,
                        allowOutsideClick: false,
                        showConfirmButton: true,
                        confirmButtonText: "Use this address anyway",
                        confirmButtonColor: "#ec250d",
                        showCancelButton: true,
                        cancelButtonText: "Let me change the address",
                        cancelButtonColor: "#3085d6",
                        type: "error",
                        html: `
            <div class="col-12">${addressLookup.street}</div>
            <div class="col-12">${addressLookup.city}, ${addressLookup.state} ${addressLookup.zipCode}</div>
            `,
                    });
                    if (value) {
                        quote.deliveryAddressVerified = false;
                        sendRequest = true;
                    }
                }
            }
            if (sendRequest) {
                await this.addQuote(quote);
                this.$emit("update:show", false);
                swal({
                    title: "Success!",
                    text: "Your quote request has been submitted.",
                    buttonsStyling: false,
                    confirmButtonClass: "btn btn-success btn-fill",
                    type: "success",
                });
            }
        },
    },
};
</script>
